import { faLayerGroup, faBuilding, faUsers, faPlug, faHandshake } from "@fortawesome/free-solid-svg-icons";
import i18n from "../translations";

// Account-level nav shown when no single agent is in context (the agents
// overview and its siblings). Workspaces / Team members are account-wide,
// not per-agent, so they live here next to Agents rather than inside an
// agent's nav.
//
// Shared so the sidebar (App.js) and the header (AppHeader.js) agree on which
// routes are "overview" pages — the header hides the per-agent bot selector
// on these, and the sidebar swaps to this nav set.
// `label` is a getter so it resolves through i18n at access time (render),
// matching the current language — these are module-level constants, so we must
// not call t() eagerly at module scope. `labelKey` is the source of truth.
export const OVERVIEW_NAV = [
  { labelKey: "nav_agents", get label() { return i18n.t(this.labelKey); }, icon: faLayerGroup, path: "/my-bots" },
  { labelKey: "nav_workspaces", get label() { return i18n.t(this.labelKey); }, icon: faBuilding, path: "/workspaces" },
  { labelKey: "nav_team_members", get label() { return i18n.t(this.labelKey); }, icon: faUsers, path: "/team-members" },
  ...(process.env.REACT_APP_MCP_ENABLED === '1' ? [{ labelKey: "mcp_connections_title", get label() { return i18n.t(this.labelKey); }, icon: faPlug, path: "/mcp-connections" }] : []),
  { labelKey: "nav_partner_program", get label() { return i18n.t(this.labelKey); }, icon: faHandshake, path: "/referral" },
];

// True on any account-level (overview) route.
export function isOverviewPath(pathname) {
  return pathname === '/mcp-connections' || OVERVIEW_NAV.some((e) => e.path === pathname);
}

// Account pages reached from the user menu (Account settings, Billing). They
// belong to neither the agent nor the workspace level, so the header and the
// sidebar both leave their current level unchanged while one of these is open
// — opening Account settings / Billing must not flip the nav or the selectors.
export const ACCOUNT_PATHS = ["/my-account", "/billing"];

export function isAccountPath(pathname) {
  // `/billing/:botId` (the per-agent billing page reached from the workspace
  // billing list or an upgrade CTA) is also an account page — opening it must
  // leave the sidebar/header level unchanged, just like `/billing`.
  return ACCOUNT_PATHS.includes(pathname) || pathname.startsWith("/billing/");
}

// Routes a user with zero agents can still reach. Everything else in the app is
// agent-scoped (the sidebar shows the per-agent nav, the header shows the bot
// selector), so a user who has no agents yet must never land there — they'd be
// looking at the nav for an agent that doesn't exist. Keep in sync with the
// account/utility routes in App.js that don't depend on a selected agent.
export const AGENT_OPTIONAL_PATHS = [
  ...OVERVIEW_NAV.map((e) => e.path), // /my-bots, /workspaces, /team-members, /referral
  ...ACCOUNT_PATHS, // /my-account, /billing
  "/help",
  "/feedback",
  "/mcp/authorize",
  "/mcp/provider-result",
  "/mcp-connections",
];

// True for any route that only makes sense once an agent exists. Used to bounce
// agent-less users (e.g. straight after registration) back to the Agents
// overview instead of dropping them into an empty agent-level page.
export function requiresAgent(pathname) {
  return !AGENT_OPTIONAL_PATHS.includes(pathname);
}
